import { useEffect, useState } from 'react';
import { api } from '../api/client.ts';

interface Module { code: string; libelle: string; actif: boolean; profils: { STOCK: boolean; AGENT: boolean } }
interface Csb { id: string; nom: string }
interface Compte { id: string; nom: string; email: string; actif: boolean; profil_saisie: 'STOCK' | 'AGENT' | null; derniere_connexion: string | null; doit_changer_mdp: boolean }
interface Droit { code: string; libelle: string; profil: boolean | null; exception: boolean | null; effectif: boolean }

/** ADMIN : verrouiller / déverrouiller chaque module (par profil ou par compte), modifier identifiants, réinitialiser les mots de passe. */
export default function AdminAcces() {
  const [modules, setModules] = useState<Module[]>([]);
  const [csbs, setCsbs] = useState<Csb[]>([]);
  const [csbId, setCsbId] = useState('');
  const [comptes, setComptes] = useState<Compte[]>([]);
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [droits, setDroits] = useState<Record<string, Droit[]>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [nouveauCode, setNouveauCode] = useState('');
  const [nouveauLibelle, setNouveauLibelle] = useState('');

  const echec = (e: any) => setErreur(e?.message ?? 'Action impossible');
  async function chargerModules() { try { setModules(await api.get<Module[]>('/admin/modules')); } catch (e) { echec(e); } }
  useEffect(() => { void chargerModules(); void api.get<Csb[]>('/admin/csb').then(setCsbs).catch(echec); }, []);
  async function chargerComptes(id: string) {
    setCsbId(id); setComptes([]); setDroits({});
    if (!id) return;
    try {
      const l = (await api.get<Compte[]>(`/admin/csb/${id}/utilisateurs`)).filter((c) => c.profil_saisie);
      setComptes(l); setEmails(Object.fromEntries(l.map((c) => [c.id, c.email])));
    } catch (e) { echec(e); }
  }
  async function reglerProfil(code: string, profil: 'STOCK' | 'AGENT', autorise: boolean) {
    setErreur(null);
    try { await api.put(`/admin/modules/${code}/profil/${profil}`, { autorise }); await chargerModules(); } catch (e) { echec(e); }
  }
  async function ajouterModule(e: React.FormEvent) {
    e.preventDefault(); setErreur(null);
    try { await api.post('/admin/modules', { code: nouveauCode, libelle: nouveauLibelle }); setNouveauCode(''); setNouveauLibelle(''); setMessage('Module enregistré (verrouillé par défaut).'); await chargerModules(); } catch (e2) { echec(e2); }
  }
  async function enregistrerEmail(c: Compte) {
    setErreur(null);
    try { await api.patch(`/admin/users/${c.id}`, { email: emails[c.id] }); setMessage('Identifiant modifié.'); await chargerComptes(csbId); } catch (e) { echec(e); }
  }
  async function reinitialiser(c: Compte, defaut: boolean) {
    setErreur(null);
    if (!confirm(`Réinitialiser le mot de passe de ${c.email} ? Un changement sera exigé à la prochaine connexion.`)) return;
    try {
      const r = await api.post<{ motDePasseTemporaire: string }>(`/admin/users/${c.id}/reinitialiser`, { defaut });
      setMessage(`Nouveau mot de passe de ${c.email} : ${r.motDePasseTemporaire} (affiché une seule fois, à communiquer au titulaire).`);
    } catch (e) { echec(e); }
  }
  async function activer(c: Compte, actif: boolean) {
    try { await api.post(`/admin/users/${c.id}/${actif ? 'reactiver' : 'desactiver'}`); await chargerComptes(csbId); } catch (e) { echec(e); }
  }
  async function voirDroits(c: Compte) {
    try { setDroits({ ...droits, [c.id]: (await api.get<{ modules: Droit[] }>(`/admin/users/${c.id}/modules`)).modules }); } catch (e) { echec(e); }
  }
  async function exception(c: Compte, code: string, v: string) {
    try { await api.put(`/admin/users/${c.id}/modules/${code}`, { autorise: v === 'heritage' ? null : v === 'oui' }); await voirDroits(c); } catch (e) { echec(e); }
  }

  return (
    <div>
      <h1>Accès des comptes Stock et Agent</h1>
      <p className="note">Chaque CSB reçoit automatiquement un compte Stock et un compte Agent. Tout module non autorisé est verrouillé par défaut (futurs modules compris).</p>
      {message && <p role="status">✅ {message}</p>}
      {erreur && <p className="messages-erreur">{erreur}</p>}

      <section className="carte">
        <h2>Modules par profil</h2>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead><tr><th>Module</th><th>Compte Stock</th><th>Compte Agent</th></tr></thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.code}>
                  <td>{m.libelle} <small>({m.code})</small></td>
                  {(['STOCK', 'AGENT'] as const).map((p) => (
                    <td key={p}><label><input type="checkbox" checked={m.profils[p]} onChange={(e) => void reglerProfil(m.code, p, e.target.checked)} /> {m.profils[p] ? 'Déverrouillé' : '🔒 Verrouillé'}</label></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form onSubmit={ajouterModule} style={{ marginTop: 12 }}>
          <b>Enregistrer un futur module</b>{' '}
          <input aria-label="Code du module" placeholder="CODE_MODULE" value={nouveauCode} onChange={(e) => setNouveauCode(e.target.value.toUpperCase())} required pattern="[A-Z0-9_]{2,40}" />{' '}
          <input aria-label="Libellé" placeholder="Libellé" value={nouveauLibelle} onChange={(e) => setNouveauLibelle(e.target.value)} required minLength={2} />{' '}
          <button type="submit">Ajouter</button>
        </form>
      </section>

      <section className="carte">
        <h2>Comptes d'un CSB</h2>
        <select aria-label="CSB" value={csbId} onChange={(e) => void chargerComptes(e.target.value)}>
          <option value="">— Choisir un CSB —</option>
          {csbs.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        {csbId && comptes.length === 0 && <p>Aucun compte Stock / Agent pour ce CSB (appliquez la migration 012).</p>}
        {comptes.map((c) => (
          <div key={c.id} className="carte" style={{ marginTop: 10 }}>
            <b>{c.profil_saisie === 'STOCK' ? 'Compte Stock' : 'Compte Agent'}</b> {c.actif ? '' : '· désactivé'} {c.doit_changer_mdp ? '· mot de passe initial non changé' : ''}
            <div style={{ margin: '8px 0' }}>
              <input aria-label="Identifiant (e-mail)" type="email" value={emails[c.id] ?? ''} onChange={(e) => setEmails({ ...emails, [c.id]: e.target.value })} style={{ minWidth: 280 }} />{' '}
              <button onClick={() => void enregistrerEmail(c)} disabled={emails[c.id] === c.email}>Enregistrer l'identifiant</button>
            </div>
            <button className="secondaire" onClick={() => void reinitialiser(c, true)}>Réinitialiser (nom du CSB)</button>{' '}
            <button className="secondaire" onClick={() => void reinitialiser(c, false)}>Réinitialiser (aléatoire)</button>{' '}
            <button className="secondaire" onClick={() => void activer(c, !c.actif)}>{c.actif ? 'Désactiver' : 'Réactiver'}</button>{' '}
            <button className="secondaire" onClick={() => void voirDroits(c)}>Droits de ce compte</button>
            {droits[c.id] && (
              <table style={{ marginTop: 8 }}>
                <thead><tr><th>Module</th><th>Exception pour ce compte</th><th>Effectif</th></tr></thead>
                <tbody>
                  {droits[c.id].map((d) => (
                    <tr key={d.code}>
                      <td>{d.libelle}</td>
                      <td>
                        <select aria-label={`Exception ${d.libelle}`} value={d.exception === null ? 'heritage' : d.exception ? 'oui' : 'non'} onChange={(e) => void exception(c, d.code, e.target.value)}>
                          <option value="heritage">Selon le profil</option><option value="oui">Déverrouillé</option><option value="non">Verrouillé</option>
                        </select>
                      </td>
                      <td>{d.effectif ? '✅ accessible' : '🔒 verrouillé'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
